import { useState } from 'react'
import { useConfirmDelete } from '../hooks/useConfirmDelete'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { fatturazioneApi, committentiApi } from '../services/api'
import { useAuth } from '../contexts/AuthContext'
import { TrendingUp, ClipboardList, ChevronDown, ChevronRight, Building2, Plus, CheckCircle, CreditCard, FileText, RefreshCw, Trash2, Download, AlertCircle, BarChart2, Receipt, Send, Pencil, X as XIcon } from 'lucide-react'
import toast from 'react-hot-toast'
import clsx from 'clsx'
import { format } from 'date-fns'
import { it } from 'date-fns/locale'

const MESI = ['', 'Gennaio', 'Febbraio', 'Marzo', 'Aprile', 'Maggio', 'Giugno',
  'Luglio', 'Agosto', 'Settembre', 'Ottobre', 'Novembre', 'Dicembre']

const MESI_SHORT = ['Gen','Feb','Mar','Apr','Mag','Giu','Lug','Ago','Set','Ott','Nov','Dic']

function MeseSelector({ value, onChange }: { value: number; onChange: (m: number) => void }) {
  return (
    <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', alignItems: 'center' }}>
      <button
        onClick={() => onChange(0)}
        style={{
          padding: '3px 10px', borderRadius: 99, fontSize: 11, fontWeight: 600, cursor: 'pointer',
          border: '1px solid',
          borderColor: value === 0 ? '#1E40AF' : '#e5e7eb',
          background: value === 0 ? '#1E40AF' : '#ffffff',
          color: value === 0 ? '#ffffff' : '#6b7280',
        }}
      >Tutti</button>
      {MESI_SHORT.map((m, i) => (
        <button
          key={i + 1}
          onClick={() => onChange(i + 1)}
          style={{
            padding: '3px 10px', borderRadius: 99, fontSize: 11, fontWeight: 600, cursor: 'pointer',
            border: '1px solid',
            borderColor: value === i + 1 ? '#1E40AF' : '#e5e7eb',
            background: value === i + 1 ? '#1E40AF' : '#ffffff',
            color: value === i + 1 ? '#ffffff' : '#6b7280',
          }}
        >{m}</button>
      ))}
    </div>
  )
}

const WO_STATO_CLS: Record<string, string> = {
  ricevuto:  'bg-slate-100 text-slate-600',
  assegnato: 'bg-slate-200 text-slate-700',
  sospeso:   'bg-amber-100 text-amber-700',
  annullato: 'bg-red-100 text-red-700',
  eseguito:  'bg-green-100 text-green-700',
}
const WO_STATO_LABEL: Record<string, string> = {
  ricevuto: 'Ricevuto', assegnato: 'Assegnato', sospeso: 'Sospeso', annullato: 'Annullato', eseguito: 'Eseguito',
}
function WoStatoBadge({ stato }: { stato: string }) {
  const cls = WO_STATO_CLS[stato] || 'bg-slate-100 text-slate-500'
  const label = WO_STATO_LABEL[stato] || stato
  return <span className={clsx('inline-flex items-center text-xs font-medium px-2 py-0.5 rounded-full', cls)}>{label}</span>
}

const STATO_CLS: Record<string, string> = {
  bozza:        'bg-slate-100 text-slate-600',
  emessa:       'bg-blue-100 text-blue-700',
  inviata_sdi:  'bg-indigo-100 text-indigo-700',
  pagata:       'bg-green-100 text-green-700',
  scaduta:      'bg-red-100 text-red-700',
  stornata:     'bg-slate-100 text-slate-400',
  in_contenzioso: 'bg-orange-100 text-orange-700',
}

type Tab = 'produzione' | 'da-inserire' | 'sal' | 'scadenzario' | 'note-credito' | 'report-iva'

export default function Fatturazione() {
  const { confirmDelete, ConfirmModal } = useConfirmDelete()
  const [tab, setTab] = useState<Tab>('produzione')
  const [anno, setAnno] = useState(new Date().getFullYear())
  const [mese, setMese] = useState(new Date().getMonth() + 1)
  const [meseDaInserire, setMeseDaInserire] = useState(0)
  const [mostraNonCompletate, setMostraNonCompletate] = useState(false)
  const [cercaPratica, setCercaPratica] = useState('')
  const [meseSal, setMeseSal] = useState(0)
  const [committenteFilter, setCommittenteFilter] = useState('')
  const [expanded, setExpanded] = useState<Set<number>>(new Set())
  const [expandedFattura, setExpandedFattura] = useState<Set<number>>(new Set())
  const [selectedWO, setSelectedWO] = useState<Set<number>>(new Set())
  const [showCreaModal, setShowCreaModal] = useState(false)
  const [statoFilter, setStatoFilter] = useState('')
  const [tipoDocFilter, setTipoDocFilter] = useState('')
  const [aggiungiPraticheId, setAggiungiPraticheId] = useState<number | null>(null)
  const [showNcModal, setShowNcModal] = useState(false)
  const [ncFatturaId, setNcFatturaId] = useState<number | null>(null)
  const [emittiSalModal, setEmittiSalModal] = useState<{ id: number; dataEmissione: string; committente: string } | null>(null)
  const [editingDate, setEditingDate] = useState<{ id: number; value: string } | null>(null)
  const [modificaSalId, setModificaSalId] = useState<number | null>(null)
  const qc = useQueryClient()
  const { user } = useAuth()
  const isAdmin = user?.role === 'admin' || user?.role === 'superadmin'

  const { data: committenti } = useQuery({
    queryKey: ['committenti-select'],
    queryFn: () => committentiApi.list({ per_page: 100 }).then(r => r.data.items),
  })

  // Tab produzione
  const { data: prodData, isLoading: prodLoading } = useQuery({
    queryKey: ['produzione', anno, mese, committenteFilter],
    queryFn: () => fatturazioneApi.produzione(anno, mese || undefined, committenteFilter ? Number(committenteFilter) : undefined).then(r => r.data),
    enabled: tab === 'produzione',
  })

  // Tab da fatturare — sempre attiva (serve anche nel modal +WO dal tab SAL)
  const { data: daFatturareData, isLoading: dfLoading, refetch: refetchDF } = useQuery({
    queryKey: ['da-inserire', committenteFilter, anno, meseDaInserire, mostraNonCompletate],
    queryFn: () => fatturazioneApi.daFatturare(
      committenteFilter ? Number(committenteFilter) : undefined,
      anno,
      meseDaInserire || undefined,
      mostraNonCompletate || undefined,
    ).then(r => r.data),
  })

  // Tab scadenzario
  const { data: scadenzarioData, isLoading: scadLoading, refetch: refetchScad } = useQuery({
    queryKey: ['scadenzario', committenteFilter],
    queryFn: () => fatturazioneApi.scadenzario(committenteFilter ? Number(committenteFilter) : undefined).then(r => r.data),
    enabled: tab === 'scadenzario',
  })

  // Tab credit control
  const { data: creditData, isLoading: creditLoading } = useQuery({
    queryKey: ['credit-control', anno],
    queryFn: () => fatturazioneApi.creditControl(anno).then(r => r.data),
    enabled: tab === 'scadenzario',
  })

  // Tab note credito
  const { data: noteData, isLoading: noteLoading, refetch: refetchNote } = useQuery({
    queryKey: ['note-credito', committenteFilter],
    queryFn: () => fatturazioneApi.noteCredito.list(undefined, committenteFilter ? Number(committenteFilter) : undefined).then(r => r.data),
    enabled: tab === 'note-credito',
  })

  // Tab report IVA
  const { data: ivaData, isLoading: ivaLoading } = useQuery({
    queryKey: ['report-iva', anno],
    queryFn: () => fatturazioneApi.reportIva(anno).then(r => r.data),
    enabled: tab === 'report-iva',
  })

  const deleteNcMut = useMutation({
    mutationFn: (id: number) => fatturazioneApi.noteCredito.delete(id),
    onSuccess: () => { refetchNote(); toast.success('Nota credito eliminata') },
    onError: () => toast.error('Errore eliminazione'),
  })

  const sollecitaMut = useMutation({
    mutationFn: () => fatturazioneApi.inviaSolleciti(),
    onSuccess: (r: any) => toast.success(`Solleciti inviati: ${r.data.inviati}`),
    onError: () => toast.error('Errore invio solleciti'),
  })

  // Tab fatture
  const { data: fattureData, isLoading: fattureLoading, refetch: refetchFatture } = useQuery({
    queryKey: ['sal', committenteFilter, statoFilter, anno, meseSal, tipoDocFilter],
    queryFn: () => fatturazioneApi.list({
      committente_id: committenteFilter || undefined,
      stato: statoFilter || undefined,
      anno,
      mese: meseSal || undefined,
      tipo_documento: tipoDocFilter || undefined,
    }).then(r => r.data),
    enabled: tab === 'sal',
  })

  const emettiMut = useMutation({
    mutationFn: (id: number) => fatturazioneApi.emetti(id),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['sal'] }); toast.success('SAL emesso') },
    onError: () => toast.error('Errore'),
  })

  const updateMut = useMutation({
    mutationFn: ({ id, data }: { id: number; data: Record<string, string> }) =>
      fatturazioneApi.update(id, data),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['sal'] }); setEditingDate(null) },
    onError: () => toast.error('Errore salvataggio data'),
  })

  const pagataMut = useMutation({
    mutationFn: ({ id, data_pagamento }: { id: number; data_pagamento: string }) =>
      fatturazioneApi.pagata(id, data_pagamento),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['sal'] }); toast.success('SAL segnato come pagato') },
    onError: () => toast.error('Errore'),
  })

  const deleteMut = useMutation({
    mutationFn: (id: number) => fatturazioneApi.delete(id),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['sal'] }); toast.success('SAL eliminato') },
    onError: () => toast.error('Errore eliminazione'),
  })

  const aggiornaSalMut = useMutation({
    mutationFn: () => fatturazioneApi.aggiornaSalOra(),
    onSuccess: () => { toast.success('SAL aggiornato'); refetchFatture() },
    onError: () => toast.error('Errore aggiornamento SAL'),
  })
  const deleteAllMut = useMutation({
    mutationFn: () => fatturazioneApi.deleteAll(),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['sal'] }); toast.success('Tutti i SAL eliminati') },
    onError: () => toast.error('Errore eliminazione'),
  })

  const aggiungiPraticheMut = useMutation({
    mutationFn: ({ salId, woIds }: { salId: number; woIds: number[] }) =>
      fatturazioneApi.aggiungiPratiche(salId, woIds),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['sal'] })
      toast.success('Pratiche aggiunte al SAL')
      setAggiungiPraticheId(null)
    },
    onError: () => toast.error('Errore aggiunta pratiche'),
  })

  const modificaSalMut = useMutation({
    mutationFn: ({ id, data }: { id: number; data: Record<string, unknown> }) => fatturazioneApi.update(id, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['sal'] })
      toast.success('SAL aggiornato')
      setModificaSalId(null)
    },
    onError: (e: any) => toast.error(e?.response?.data?.detail || 'Errore aggiornamento SAL'),
  })

  const downloadPdf = async (id: number, numero: string, tipoDoc?: string) => {
    try {
      const res = await fatturazioneApi.pdf(id)
      const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }))
      const a = document.createElement('a')
      a.href = url
      const prefix = tipoDoc === 'sal' ? 'sal' : 'fattura'
      a.download = `${prefix}_${numero}.pdf`
      a.click()
      URL.revokeObjectURL(url)
    } catch {
      toast.error('Errore generazione PDF')
    }
  }

  const toggleExpand = (id: number) => setExpanded(prev => {
    const next = new Set(prev); next.has(id) ? next.delete(id) : next.add(id); return next
  })

  const toggleFattura = (id: number) => setExpandedFattura(prev => {
    const next = new Set(prev); next.has(id) ? next.delete(id) : next.add(id); return next
  })

  const toggleWO = (id: number) => setSelectedWO(prev => {
    const next = new Set(prev); next.has(id) ? next.delete(id) : next.add(id); return next
  })

  const gruppiProduzione = prodData?.committenti ?? []
  const daFatturareTutti: any[] = daFatturareData?.items ?? []
  const qPratica = cercaPratica.trim().toLowerCase()
  const daFatturareItems: any[] = qPratica
    ? daFatturareTutti.filter(w => String(w.numero_progressivo ?? '').toLowerCase().includes(qPratica))
    : daFatturareTutti
  const fattureItems: any[] = fattureData?.items ?? []

  const selectedWOData = daFatturareTutti.filter(w => selectedWO.has(w.id))
  const selectedCommittenteId = selectedWOData.length > 0 ? selectedWOData[0].committente_id : null

  return (
    <div className="space-y-4">
      {ConfirmModal}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">SAL</h1>
          <p className="text-slate-500 text-sm">Stato Avanzamento Lavori</p>
        </div>
        <div className="flex gap-2">
          {tab === 'da-inserire' && selectedWO.size > 0 && (
            <button onClick={() => setShowCreaModal(true)} className="btn-primary flex items-center gap-2">
              <Plus size={16} /> Crea SAL ({selectedWO.size} WO)
            </button>
          )}
          {tab === 'sal' && (
            <button
              onClick={() => aggiornaSalMut.mutate()}
              className="btn-secondary flex items-center gap-2"
              disabled={aggiornaSalMut.isPending}
              title="Aggrega subito le pratiche eseguite nei SAL"
            >
              <RefreshCw size={15} className={aggiornaSalMut.isPending ? 'animate-spin' : ''} />
              {aggiornaSalMut.isPending ? 'Aggiornamento...' : 'Aggiorna SAL'}
            </button>
          )}
          {tab === 'sal' && isAdmin && (
            <button
              onClick={() => confirmDelete('Eliminare TUTTI i SAL? Operazione irreversibile.', () => deleteAllMut.mutate())}
              className="btn-secondary flex items-center gap-2"
              style={{ color: '#ef4444' }}
              disabled={deleteAllMut.isPending}
            >
              <Trash2 size={15} /> Cancella tutto
            </button>
          )}
          {tab === 'scadenzario' && isAdmin && (
            <button
              onClick={() => { if (window.confirm('Inviare solleciti automatici a tutte le fatture scadute?')) sollecitaMut.mutate() }}
              className="btn-secondary flex items-center gap-2"
              disabled={sollecitaMut.isPending}
            >
              <Send size={15} className={sollecitaMut.isPending ? 'animate-pulse' : ''} />
              {sollecitaMut.isPending ? 'Invio...' : 'Invia solleciti'}
            </button>
          )}
          {tab === 'note-credito' && isAdmin && (
            <button onClick={() => setShowNcModal(true)} className="btn-primary flex items-center gap-2">
              <Plus size={16} /> Nuova nota credito
            </button>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 border-b border-slate-200">
        {([
          ['produzione', 'Riepilogo Produzione', ClipboardList],
          ['da-inserire', 'Da Inserire', FileText],
          ['sal', 'SAL', TrendingUp],
          ['scadenzario', 'Scadenzario', AlertCircle],
          ['note-credito', 'Note Credito', Receipt],
          ['report-iva', 'Report IVA', BarChart2],
        ] as [Tab, string, any][]).map(([key, label, Icon]) => (
          <button key={key} onClick={() => setTab(key)}
            className={clsx('flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors',
              tab === key ? 'border-blue-600 text-blue-600' : 'border-transparent text-slate-500 hover:text-slate-700'
            )}>
            <Icon size={15} />{label}
            {key === 'da-inserire' && daFatturareData?.total > 0 && (
              <span className="bg-orange-500 text-white text-xs rounded-full px-1.5">{daFatturareData.total}</span>
            )}
          </button>
        ))}
      </div>

      {/* Filtri condivisi */}
      <div className="card p-4 flex flex-col gap-3">
        <div className="flex gap-3 flex-wrap items-center">
          {/* Anno — tutte le tab */}
          <select className="select w-28" value={anno} onChange={(e) => setAnno(Number(e.target.value))}>
            {[2024, 2025, 2026, 2027].map(y => <option key={y} value={y}>{y}</option>)}
          </select>

          {/* Mese select — solo produzione */}
          {tab === 'produzione' && (
            <select className="select w-36" value={mese} onChange={(e) => setMese(Number(e.target.value))}>
              <option value={0}>Tutto l'anno</option>
              {MESI.slice(1).map((m, i) => <option key={i + 1} value={i + 1}>{m}</option>)}
            </select>
          )}

          {/* Filtri aggiuntivi SAL */}
          {tab === 'sal' && (
            <>
              <select className="select w-36" value={statoFilter} onChange={(e) => setStatoFilter(e.target.value)}>
                <option value="">Tutti gli stati</option>
                {['bozza', 'emessa', 'pagata', 'scaduta', 'stornata'].map(s => (
                  <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>
                ))}
              </select>
              <select className="select w-36" value={tipoDocFilter} onChange={(e) => setTipoDocFilter(e.target.value)}>
                <option value="">SAL + Fatture</option>
                <option value="sal">Solo SAL</option>
                <option value="fattura">Solo Fatture</option>
              </select>
            </>
          )}

          <select className="select w-52" value={committenteFilter} onChange={(e) => { setCommittenteFilter(e.target.value); setSelectedWO(new Set()) }}>
            <option value="">Tutti i committenti</option>
            {committenti?.map((c: { id: number; ragione_sociale: string }) => (
              <option key={c.id} value={c.id}>{c.ragione_sociale}</option>
            ))}
          </select>
          <button onClick={() => {
            if (tab === 'sal') refetchFatture()
            else if (tab === 'scadenzario') refetchScad()
            else if (tab === 'note-credito') refetchNote()
            else refetchDF()
          }} className="btn-ghost px-2 py-1.5" title="Aggiorna">
            <RefreshCw size={14} />
          </button>
        </div>

        {/* Pill mese — Da Inserire */}
        {tab === 'da-inserire' && (
          <MeseSelector value={meseDaInserire} onChange={setMeseDaInserire} />
        )}

        {/* Pill mese — SAL */}
        {tab === 'sal' && (
          <MeseSelector value={meseSal} onChange={setMeseSal} />
        )}
      </div>

      {/* ── TAB: PRODUZIONE ─────────────────────────────────────────────── */}
      {tab === 'produzione' && (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
            <div className="card p-5 flex items-center justify-between">
              <div><p className="text-sm text-slate-500">Pratiche eseguite</p><p className="text-3xl font-bold text-blue-600 mt-0.5">{prodData?.totale_pratiche ?? '—'}</p></div>
              <ClipboardList size={24} className="text-blue-600 opacity-60" />
            </div>
            <div className="card p-5 flex items-center justify-between">
              <div><p className="text-sm text-slate-500">Valore stimato</p><p className="text-3xl font-bold text-green-600 mt-0.5">€{(prodData?.valore_totale ?? 0).toLocaleString('it-IT', { minimumFractionDigits: 2 })}</p></div>
              <TrendingUp size={24} className="text-green-600 opacity-60" />
            </div>
            <div className="card p-5 flex items-center justify-between">
              <div><p className="text-sm text-slate-500">Committenti attivi</p><p className="text-3xl font-bold text-indigo-600 mt-0.5">{gruppiProduzione.length}</p></div>
              <Building2 size={24} className="text-indigo-600 opacity-60" />
            </div>
          </div>

          {prodLoading ? (
            <div className="card p-10 text-center text-slate-400">Caricamento...</div>
          ) : gruppiProduzione.length === 0 ? (
            <div className="card p-10 text-center text-slate-400">Nessuna pratica eseguita nel periodo selezionato</div>
          ) : (
            <div className="space-y-2">
              {gruppiProduzione.map((g: any) => {
                const isOpen = expanded.has(g.committente_id)
                return (
                  <div key={g.committente_id} className="card overflow-hidden">
                    <button className="w-full flex items-center gap-3 px-5 py-4 hover:bg-slate-50/50 transition-colors text-left" onClick={() => toggleExpand(g.committente_id)}>
                      {isOpen ? <ChevronDown size={16} className="text-slate-400 flex-shrink-0" /> : <ChevronRight size={16} className="text-slate-400 flex-shrink-0" />}
                      <div className="w-8 h-8 rounded-lg bg-blue-100 flex items-center justify-center flex-shrink-0"><Building2 size={15} className="text-blue-700" /></div>
                      <div className="flex-1 min-w-0">
                        <div className="font-semibold text-slate-900 text-sm">{g.committente}</div>
                        <div className="text-xs text-slate-400">{g.totale_pratiche} pratiche</div>
                      </div>
                      <div className="text-right flex-shrink-0">
                        {g.valore_stimato > 0 && <div className="font-bold text-green-700 text-sm">€{g.valore_stimato.toLocaleString('it-IT', { minimumFractionDigits: 2 })}</div>}
                      </div>
                    </button>
                    {isOpen && (
                      <div className="border-t border-slate-100 overflow-x-auto">
                        <table className="w-full text-xs">
                          <thead><tr className="bg-slate-50/80">
                            <th className="text-left px-3 py-2 font-semibold text-slate-500 whitespace-nowrap">N° Pratica</th>
                            <th className="text-left px-3 py-2 font-semibold text-slate-500 whitespace-nowrap">Cod. Sito</th>
                            <th className="text-left px-3 py-2 font-semibold text-slate-500 whitespace-nowrap">Denominazione</th>
                            <th className="text-left px-3 py-2 font-semibold text-slate-500 whitespace-nowrap">Comune</th>
                            <th className="text-left px-3 py-2 font-semibold text-slate-500 whitespace-nowrap">Indirizzo</th>
                            <th className="text-left px-3 py-2 font-semibold text-slate-500 whitespace-nowrap">Tipologia</th>
                            <th className="text-left px-3 py-2 font-semibold text-slate-500 whitespace-nowrap">Data</th>
                            <th className="text-right px-3 py-2 font-semibold text-slate-500 whitespace-nowrap">Valore</th>
                          </tr></thead>
                          <tbody className="divide-y divide-slate-50">
                            {g.pratiche.map((p: any) => (
                              <tr key={p.id} className="hover:bg-slate-50">
                                <td className="px-3 py-2 font-mono text-blue-700 font-medium whitespace-nowrap">{p.numero || `#${p.id}`}</td>
                                <td className="px-3 py-2 text-slate-500 whitespace-nowrap">{p.codice_sito || '—'}</td>
                                <td className="px-3 py-2 text-slate-700 max-w-[160px] truncate" title={p.denominazione_sito}>{p.denominazione_sito || '—'}</td>
                                <td className="px-3 py-2 text-slate-600 whitespace-nowrap">{p.cliente_comune || '—'}</td>
                                <td className="px-3 py-2 text-slate-500 max-w-[140px] truncate" title={p.cliente_indirizzo}>{p.cliente_indirizzo || '—'}</td>
                                <td className="px-3 py-2 text-slate-600 capitalize whitespace-nowrap">{p.tipologia?.replace(/_/g, ' ') || '—'}</td>
                                <td className="px-3 py-2 text-slate-400 whitespace-nowrap">{p.data ? new Date(p.data).toLocaleDateString('it-IT') : '—'}</td>
                                <td className="px-3 py-2 text-right whitespace-nowrap">
                                  {p.valore > 0 ? <span style={{ fontWeight: 700, color: '#15803d' }}>€{p.valore.toFixed(2)}</span> : <span className="text-slate-300">—</span>}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                          {g.valore_stimato > 0 && (
                            <tfoot><tr className="bg-green-50/50 border-t">
                              <td colSpan={8} className="px-3 py-2 text-sm font-semibold text-slate-700">Totale</td>
                              <td className="px-3 py-2 text-right font-bold text-green-700">€{g.valore_stimato.toLocaleString('it-IT', { minimumFractionDigits: 2 })}</td>
                            </tr></tfoot>
                          )}
                        </table>
                      </div>
                    )}
                  </div>
                )
              })}
            </div>
          )}
        </>
      )}

      {/* ── TAB: DA FATTURARE ───────────────────────────────────────────── */}
      {tab === 'da-inserire' && (
        <>
          <div className="grid grid-cols-4 gap-4">
            <div className="card p-4 flex items-center justify-between">
              <div><p className="text-sm text-slate-500">Totale eseguiti</p><p className="text-2xl font-bold text-slate-700">{daFatturareData?.total ?? '—'}</p></div>
              <FileText size={20} className="text-slate-400 opacity-60" />
            </div>
            <div className="card p-4 flex items-center justify-between">
              <div><p className="text-sm text-slate-500">Da inserire</p><p className="text-2xl font-bold text-orange-600">{daFatturareData?.totale_da_inserire ?? '—'}</p></div>
              <FileText size={20} className="text-orange-400 opacity-60" />
            </div>
            <div className="card p-4 flex items-center justify-between">
              <div><p className="text-sm text-slate-500">Già in SAL bozza</p><p className="text-2xl font-bold text-green-600">{daFatturareData?.totale_in_bozza ?? '—'}</p></div>
              <CheckCircle size={20} className="text-green-500 opacity-60" />
            </div>
            <div className="card p-4 flex items-center justify-between">
              <div><p className="text-sm text-slate-500">Valore totale periodo</p><p className="text-2xl font-bold text-green-600">€{(daFatturareData?.valore_totale ?? 0).toLocaleString('it-IT', { minimumFractionDigits: 2 })}</p></div>
              <TrendingUp size={20} className="text-green-600 opacity-60" />
            </div>
          </div>

          {selectedWO.size > 0 && (
            <div className="card p-3 flex items-center gap-3 border-blue-200 bg-blue-50">
              <span className="text-sm font-semibold text-blue-700">{selectedWO.size} WO selezionati — €{selectedWOData.reduce((s: number, w: any) => s + (w.valore_stimato || 0), 0).toFixed(2)}</span>
              {selectedCommittenteId && selectedWOData.every((w: any) => w.committente_id === selectedCommittenteId) ? (
                <button onClick={() => setShowCreaModal(true)} className="btn-primary text-sm ml-auto">
                  <Plus size={14} className="mr-1" /> Crea Fattura
                </button>
              ) : (
                <span className="text-xs text-orange-600 ml-auto">Seleziona WO dello stesso committente per fatturare</span>
              )}
              <button onClick={() => setSelectedWO(new Set())} className="btn-ghost text-xs px-2">Deseleziona</button>
            </div>
          )}

          <div className="flex items-center gap-2 px-1">
            <label className="flex items-center gap-2 text-sm text-slate-600 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={mostraNonCompletate}
                onChange={(e) => setMostraNonCompletate(e.target.checked)}
              />
              Mostra pratiche non completate
            </label>
            <input
              type="search"
              value={cercaPratica}
              onChange={(e) => setCercaPratica(e.target.value)}
              placeholder="Cerca n° pratica..."
              className="input ml-auto w-56 text-sm"
            />
            {mostraNonCompletate && (
              <span className="text-xs text-slate-400">(sospeso / ricevuto / assegnato / annullato inclusi nel periodo — selezionabili manualmente)</span>
            )}
          </div>

          <div className="card overflow-hidden">
            <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="border-b bg-slate-50/50">
                <th className="px-3 py-3 w-8"><input type="checkbox" onChange={(e) => {
                  if (e.target.checked) setSelectedWO(new Set(daFatturareItems.map((w: any) => w.id)))
                  else setSelectedWO(new Set())
                }} checked={selectedWO.size === daFatturareItems.length && daFatturareItems.length > 0} /></th>
                <th className="text-left px-4 py-3 font-semibold text-slate-600 whitespace-nowrap">N° Pratica</th>
                <th className="text-left px-4 py-3 font-semibold text-slate-600 whitespace-nowrap">Cod. Sito</th>
                <th className="text-left px-4 py-3 font-semibold text-slate-600 whitespace-nowrap">Denominazione</th>
                <th className="text-left px-4 py-3 font-semibold text-slate-600 whitespace-nowrap">Comune</th>
                <th className="text-left px-4 py-3 font-semibold text-slate-600 whitespace-nowrap">Indirizzo</th>
                <th className="text-left px-4 py-3 font-semibold text-slate-600 whitespace-nowrap">Committente</th>
                <th className="text-left px-4 py-3 font-semibold text-slate-600 whitespace-nowrap">Tipologia</th>
                <th className="text-left px-4 py-3 font-semibold text-slate-600 whitespace-nowrap">Data esec.</th>
                {mostraNonCompletate && (
                  <th className="text-left px-4 py-3 font-semibold text-slate-600 whitespace-nowrap">Stato</th>
                )}
                <th className="text-left px-4 py-3 font-semibold text-slate-600 whitespace-nowrap">SAL</th>
                <th className="text-right px-4 py-3 font-semibold text-slate-600 whitespace-nowrap">Valore €</th>
              </tr></thead>
              <tbody className="divide-y divide-slate-100">
                {dfLoading ? (
                  <tr><td colSpan={mostraNonCompletate ? 11 : 10} className="py-10 text-center text-slate-400">Caricamento...</td></tr>
                ) : daFatturareItems.length === 0 ? (
                  <tr><td colSpan={mostraNonCompletate ? 11 : 10} className="py-10 text-center text-slate-400">{qPratica ? 'Nessuna pratica trovata' : 'Nessun WO da fatturare'}</td></tr>
                ) : daFatturareItems.map((w: any) => (
                  <tr key={w.id} className={clsx('transition-colors', w.in_sal_bozza ? 'bg-green-50/40 hover:bg-green-50' : 'hover:bg-slate-50', selectedWO.has(w.id) && 'bg-blue-50')}>
                    <td className="px-3 py-2.5"><input type="checkbox" checked={selectedWO.has(w.id)} onChange={() => toggleWO(w.id)} disabled={w.in_sal_bozza} /></td>
                    <td className="px-4 py-2.5 font-mono text-xs text-blue-700 font-semibold whitespace-nowrap">{w.numero_progressivo}</td>
                    <td className="px-4 py-2.5 text-xs text-slate-500 whitespace-nowrap">{w.codice_sito || '—'}</td>
                    <td className="px-4 py-2.5 text-xs text-slate-700 max-w-[160px] truncate" title={w.denominazione_sito}>{w.denominazione_sito || '—'}</td>
                    <td className="px-4 py-2.5 text-xs text-slate-600 whitespace-nowrap">{w.cliente_comune || '—'}</td>
                    <td className="px-4 py-2.5 text-xs text-slate-500 max-w-[140px] truncate" title={w.cliente_indirizzo}>{w.cliente_indirizzo || '—'}</td>
                    <td className="px-4 py-2.5 text-sm text-slate-700 whitespace-nowrap">{w.committente_nome}</td>
                    <td className="px-4 py-2.5 text-xs text-slate-600 capitalize whitespace-nowrap">{w.tipologia?.replace(/_/g, ' ') || '—'}</td>
                    <td className="px-4 py-2.5 text-xs text-slate-400 whitespace-nowrap">{w.data_esecuzione ? new Date(w.data_esecuzione).toLocaleDateString('it-IT') : '—'}</td>
                    {mostraNonCompletate && (
                      <td className="px-4 py-2.5 whitespace-nowrap"><WoStatoBadge stato={w.stato} /></td>
                    )}
                    <td className="px-4 py-2.5 whitespace-nowrap">
                      {w.in_sal_bozza
                        ? <span className="inline-flex items-center gap-1 text-xs font-medium text-green-700 bg-green-100 px-2 py-0.5 rounded-full" title={w.numero_sal}><CheckCircle size={11} />{w.numero_sal}</span>
                        : <span className="text-xs text-orange-500 font-medium">Da inserire</span>}
                    </td>
                    <td className="px-4 py-2.5 text-right font-semibold text-green-700 whitespace-nowrap">{w.valore_stimato > 0 ? `€${w.valore_stimato.toFixed(2)}` : <span className="text-slate-300">—</span>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
          </div>
        </>
      )}

      {/* ── TAB: FATTURE EMESSE ─────────────────────────────────────────── */}
      {tab === 'sal' && (
        <div className="card overflow-hidden">
          <table className="w-full text-sm">
            <thead><tr className="border-b bg-slate-50/50">
              <th className="w-8 px-2 py-3" />
              <th className="text-left px-3 py-3 font-semibold text-slate-600">Tipo</th>
              <th className="text-left px-4 py-3 font-semibold text-slate-600">N° SAL/Fattura</th>
              <th className="text-left px-4 py-3 font-semibold text-slate-600">Committente</th>
              <th className="text-left px-4 py-3 font-semibold text-slate-600">Periodo</th>
              <th className="text-left px-4 py-3 font-semibold text-slate-600">Emissione</th>
              <th className="text-center px-3 py-3 font-semibold text-slate-600">Pratiche</th>
              <th className="text-right px-4 py-3 font-semibold text-slate-600">Totale</th>
              <th className="text-left px-4 py-3 font-semibold text-slate-600">Stato</th>
              <th className="px-4 py-3 font-semibold text-slate-600">Azioni</th>
            </tr></thead>
            <tbody>
              {fattureLoading ? (
                <tr><td colSpan={10} className="py-10 text-center text-slate-400">Caricamento...</td></tr>
              ) : fattureItems.length === 0 ? (
                <tr><td colSpan={10} className="py-10 text-center text-slate-400">Nessun documento trovato</td></tr>
              ) : fattureItems.map((f: any) => {
                const isOpen = expandedFattura.has(f.id)
                const isSal = f.tipo_documento === 'sal'
                const periodo = (f.periodo_da && f.periodo_a)
                  ? `${new Date(f.periodo_da).toLocaleDateString('it-IT')} → ${new Date(f.periodo_a).toLocaleDateString('it-IT')}`
                  : '—'
                return (
                  <>
                    <tr key={f.id} className="hover:bg-slate-50 transition-colors border-b border-slate-100">
                      <td className="px-2 py-3 text-center">
                        <button onClick={() => toggleFattura(f.id)} className="text-slate-400 hover:text-slate-600">
                          {isOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                        </button>
                      </td>
                      <td className="px-3 py-3">
                        <span className={clsx('inline-flex items-center px-2 py-0.5 rounded-full text-xs font-bold',
                          isSal ? 'bg-orange-100 text-orange-700' : 'bg-blue-100 text-blue-700')}>
                          {isSal ? 'SAL' : 'FAT'}
                        </span>
                      </td>
                      <td className="px-4 py-3 font-mono text-xs font-semibold text-blue-700">{f.numero_fattura}</td>
                      <td className="px-4 py-3 text-sm text-slate-700">{f.committente_nome}</td>
                      <td className="px-4 py-3 text-xs text-slate-500">{periodo}</td>
                      <td className="px-4 py-3 text-xs text-slate-500">
                        {editingDate?.id === f.id ? (
                          <input
                            type="date"
                            className="input py-0.5 px-1 text-xs w-32"
                            value={editingDate!.value}
                            autoFocus
                            onChange={e => setEditingDate({ id: f.id, value: e.target.value })}
                            onBlur={() => {
                              if (editingDate!.value) updateMut.mutate({ id: f.id, data: { data_emissione: editingDate!.value } })
                              else setEditingDate(null)
                            }}
                            onKeyDown={e => {
                              if (e.key === 'Enter' && editingDate!.value) updateMut.mutate({ id: f.id, data: { data_emissione: editingDate!.value } })
                              if (e.key === 'Escape') setEditingDate(null)
                            }}
                          />
                        ) : (
                          <span
                            className="cursor-pointer hover:text-blue-600 hover:underline"
                            title="Clicca per modificare la data di emissione"
                            onClick={() => setEditingDate({ id: f.id, value: f.data_emissione ?? format(new Date(), 'yyyy-MM-dd') })}
                          >
                            {f.data_emissione ? new Date(f.data_emissione).toLocaleDateString('it-IT') : '—'}
                          </span>
                        )}
                      </td>
                      <td className="px-3 py-3 text-center text-xs text-slate-600">{(f.work_order_ids?.length ?? 0)}</td>
                      <td className="px-4 py-3 text-right font-bold text-slate-800">€{f.importo_totale?.toFixed(2)}</td>
                      <td className="px-4 py-3">
                        <span className={clsx('badge text-xs', STATO_CLS[f.stato] || 'bg-slate-100')}>{f.stato}</span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-1">
                          {f.stato === 'bozza' && (
                            <button
                              onClick={() => isSal
                                ? setEmittiSalModal({ id: f.id, dataEmissione: f.data_emissione, committente: f.committente_nome })
                                : (window.confirm('Emettere la fattura?') && emettiMut.mutate(f.id))
                              }
                              className="btn-ghost px-2 py-1 text-blue-600 text-xs flex items-center gap-1"
                              title="Emetti"
                            >
                              <CheckCircle size={13} /> Emetti
                            </button>
                          )}
                          {isSal && f.stato === 'bozza' && (
                            <button
                              onClick={() => setAggiungiPraticheId(f.id)}
                              className="btn-ghost px-2 py-1 text-orange-600 text-xs flex items-center gap-1"
                              title="Aggiungi pratiche al SAL"
                            >
                              <Plus size={13} /> +WO
                            </button>
                          )}
                          {(f.stato === 'bozza' || f.stato === 'emessa') && (
                            <button
                              onClick={() => setModificaSalId(f.id)}
                              className="btn-ghost px-2 py-1 text-slate-600 text-xs flex items-center gap-1"
                              title={isSal
                                ? 'Modifica SAL (righe, importi, N° SAL, date) — se già emesso torna in bozza per reinvio'
                                : 'Modifica fattura (numero, date, periodo)'}
                            >
                              <Pencil size={13} /> Modifica
                            </button>
                          )}
                          {(f.stato === 'emessa' || f.stato === 'inviata_sdi' || f.stato === 'scaduta') && (
                            <button
                              onClick={() => {
                                const dp = window.prompt('Data pagamento (YYYY-MM-DD):', format(new Date(), 'yyyy-MM-dd'))
                                if (dp) pagataMut.mutate({ id: f.id, data_pagamento: dp })
                              }}
                              className="btn-ghost px-2 py-1 text-green-600 text-xs flex items-center gap-1"
                              title="Segna pagata"
                            >
                              <CreditCard size={13} /> Pagata
                            </button>
                          )}
                          <button
                            onClick={() => downloadPdf(f.id, f.numero_fattura, f.tipo_documento)}
                            className="btn-ghost px-2 py-1 text-indigo-600 text-xs flex items-center gap-1"
                            title={isSal ? 'Scarica PDF SAL' : 'Scarica PDF Fattura'}
                          >
                            <Download size={13} /> PDF
                          </button>
                          {isAdmin && (
                            <button
                              onClick={() => confirmDelete(`Eliminare ${isSal ? 'SAL' : 'fattura'} ${f.numero_fattura}?`, () => deleteMut.mutate(f.id))}
                              className="btn-ghost px-2 py-1 text-red-500 text-xs flex items-center gap-1"
                              title={isSal ? 'Elimina SAL' : 'Elimina fattura'}
                              disabled={deleteMut.isPending}
                            >
                              <Trash2 size={13} />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                    {isOpen && f.wo_details?.length > 0 && (
                      <tr key={`${f.id}-detail`} className="bg-slate-50/70">
                        <td colSpan={10} className="px-6 pb-3 pt-1">
                          {isSal && (
                            <div className="mb-2 text-xs text-orange-700 font-semibold">
                              🧾 SAL N° {f.numero_sal} · {f.work_order_ids?.length ?? 0} pratiche incluse
                            </div>
                          )}
                          <div className="overflow-x-auto rounded border border-slate-200">
                            <table className="w-full text-xs">
                              <thead>
                                <tr className="bg-slate-100 text-slate-500">
                                  <th className="text-left px-3 py-2 font-semibold whitespace-nowrap">N° WO</th>
                                  <th className="text-left px-3 py-2 font-semibold whitespace-nowrap">Cod. Sito</th>
                                  <th className="text-left px-3 py-2 font-semibold whitespace-nowrap">Denominazione</th>
                                  <th className="text-left px-3 py-2 font-semibold whitespace-nowrap">Comune</th>
                                  <th className="text-left px-3 py-2 font-semibold whitespace-nowrap">Indirizzo</th>
                                  <th className="text-left px-3 py-2 font-semibold whitespace-nowrap">Tipologia</th>
                                </tr>
                              </thead>
                              <tbody className="divide-y divide-slate-100">
                                {f.wo_details.map((riga: any) => (
                                  <tr key={riga.wo_id} className="hover:bg-white">
                                    <td className="px-3 py-1.5 font-mono text-blue-700 font-medium whitespace-nowrap">{riga.numero_progressivo}</td>
                                    <td className="px-3 py-1.5 text-slate-500 whitespace-nowrap">{riga.codice_sito || '—'}</td>
                                    <td className="px-3 py-1.5 text-slate-700 max-w-[160px] truncate" title={riga.denominazione_sito}>{riga.denominazione_sito || '—'}</td>
                                    <td className="px-3 py-1.5 text-slate-600 whitespace-nowrap">{riga.cliente_comune || '—'}</td>
                                    <td className="px-3 py-1.5 text-slate-500 max-w-[140px] truncate" title={riga.cliente_indirizzo}>{riga.cliente_indirizzo || '—'}</td>
                                    <td className="px-3 py-1.5 text-slate-600 capitalize whitespace-nowrap">{riga.tipologia?.replace(/_/g, ' ') || '—'}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </td>
                      </tr>
                    )}
                  </>
                )
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* ── TAB: SCADENZARIO ─────────────────────────────────────────────── */}
      {tab === 'scadenzario' && (
        <div className="space-y-4">
          {scadLoading ? <div className="card p-8 text-center text-slate-400">Caricamento...</div> : (
            <>
              {/* KPI aging */}
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                {[
                  { label: 'Scaduto totale', val: scadenzarioData?.totale_credito_scaduto ?? 0, color: '#dc2626' },
                  { label: 'In scadenza 30gg', val: scadenzarioData?.totale_in_scadenza ?? 0, color: '#f59e0b' },
                  { label: 'Scadute 0-30gg', val: (scadenzarioData?.scadute_0_30gg ?? []).reduce((s: number, f: any) => s + f.importo_totale, 0), color: '#f97316' },
                  { label: 'Scadute >90gg', val: (scadenzarioData?.scadute_oltre_90gg ?? []).reduce((s: number, f: any) => s + f.importo_totale, 0), color: '#7f1d1d' },
                ].map(({ label, val, color }) => (
                  <div key={label} className="card p-4">
                    <div className="text-xs text-slate-500 mb-1">{label}</div>
                    <div className="text-xl font-bold" style={{ color }}>€{(val as number).toLocaleString('it-IT', { minimumFractionDigits: 2 })}</div>
                  </div>
                ))}
              </div>

              {/* Aging table */}
              {([
                { key: 'scadute_oltre_90gg', label: 'Oltre 90 giorni', color: '#7f1d1d', bg: '#fef2f2' },
                { key: 'scadute_60_90gg', label: '60–90 giorni', color: '#dc2626', bg: '#fef2f2' },
                { key: 'scadute_30_60gg', label: '30–60 giorni', color: '#f97316', bg: '#fff7ed' },
                { key: 'scadute_0_30gg', label: '0–30 giorni', color: '#d97706', bg: '#fffbeb' },
                { key: 'in_scadenza_30gg', label: 'In scadenza prossimi 30gg', color: '#0369a1', bg: '#f0f9ff' },
              ] as any[]).map(({ key, label, color, bg }) => {
                const items: any[] = scadenzarioData?.[key] ?? []
                if (!items.length) return null
                return (
                  <div key={key} className="card overflow-hidden">
                    <div className="px-4 py-2 text-xs font-bold uppercase tracking-wide" style={{ background: bg, color }}>
                      {label} — {items.length} fatture — €{items.reduce((s: number, f: any) => s + f.importo_totale, 0).toLocaleString('it-IT', { minimumFractionDigits: 2 })}
                    </div>
                    <table className="w-full text-sm">
                      <thead><tr className="bg-slate-50 text-xs text-slate-500">
                        <th className="text-left px-4 py-2">N° Fattura</th>
                        <th className="text-left px-4 py-2">Committente</th>
                        <th className="text-center px-4 py-2">Scadenza</th>
                        <th className="text-center px-4 py-2">Ritardo</th>
                        <th className="text-right px-4 py-2">Importo</th>
                        <th className="text-center px-4 py-2">Stato</th>
                      </tr></thead>
                      <tbody className="divide-y divide-slate-100">
                        {items.map((f: any) => (
                          <tr key={f.id} className="hover:bg-slate-50">
                            <td className="px-4 py-2 font-mono text-blue-700 text-xs">{f.numero_fattura}</td>
                            <td className="px-4 py-2 text-slate-700">{f.committente_nome}</td>
                            <td className="px-4 py-2 text-center text-slate-500 text-xs">{f.data_scadenza}</td>
                            <td className="px-4 py-2 text-center font-medium text-xs" style={{ color }}>{f.giorni_ritardo > 0 ? `+${f.giorni_ritardo}gg` : `${f.giorni_ritardo}gg`}</td>
                            <td className="px-4 py-2 text-right font-semibold">€{f.importo_totale.toLocaleString('it-IT', { minimumFractionDigits: 2 })}</td>
                            <td className="px-4 py-2 text-center"><span className={`text-xs px-2 py-0.5 rounded-full ${STATO_CLS[f.stato] || 'bg-slate-100'}`}>{f.stato}</span></td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )
              })}

              {/* Credit control per committente */}
              {!creditLoading && creditData && creditData.length > 0 && (
                <div className="card overflow-hidden">
                  <div className="px-4 py-3 border-b border-slate-100">
                    <h3 className="text-sm font-semibold">Credit Control per Committente — {anno}</h3>
                  </div>
                  <table className="w-full text-sm">
                    <thead><tr className="bg-slate-50 text-xs text-slate-500">
                      <th className="text-left px-4 py-2">Committente</th>
                      <th className="text-right px-4 py-2">Emesso</th>
                      <th className="text-right px-4 py-2">Pagato</th>
                      <th className="text-right px-4 py-2 text-orange-600">In scadenza 30gg</th>
                      <th className="text-right px-4 py-2 text-red-600">Scaduto</th>
                      <th className="text-center px-4 py-2">N° scadute</th>
                    </tr></thead>
                    <tbody className="divide-y divide-slate-100">
                      {creditData.map((c: any) => (
                        <tr key={c.committente_id} className={`hover:bg-slate-50 ${c.totale_scaduto > 0 ? 'bg-red-50/30' : ''}`}>
                          <td className="px-4 py-2 font-medium">{c.committente_nome}</td>
                          <td className="px-4 py-2 text-right text-slate-600">€{c.totale_emesso.toLocaleString('it-IT', { minimumFractionDigits: 2 })}</td>
                          <td className="px-4 py-2 text-right text-green-700">€{c.totale_pagato.toLocaleString('it-IT', { minimumFractionDigits: 2 })}</td>
                          <td className="px-4 py-2 text-right text-orange-600 font-medium">{c.in_scadenza_30gg > 0 ? `€${c.in_scadenza_30gg.toLocaleString('it-IT', { minimumFractionDigits: 2 })}` : '—'}</td>
                          <td className="px-4 py-2 text-right text-red-600 font-bold">{c.totale_scaduto > 0 ? `€${c.totale_scaduto.toLocaleString('it-IT', { minimumFractionDigits: 2 })}` : '—'}</td>
                          <td className="px-4 py-2 text-center">{c.n_scadute > 0 ? <span className="badge bg-red-100 text-red-700">{c.n_scadute}</span> : '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* ── TAB: NOTE CREDITO ────────────────────────────────────────────── */}
      {tab === 'note-credito' && (
        <div className="card overflow-hidden">
          {noteLoading ? <div className="p-8 text-center text-slate-400">Caricamento...</div> : (
            <>
              {(!noteData || noteData.length === 0) ? (
                <div className="p-8 text-center text-slate-400">
                  <Receipt size={32} className="mx-auto mb-3 opacity-40" />
                  <p className="text-sm">Nessuna nota di credito</p>
                </div>
              ) : (
                <table className="w-full text-sm">
                  <thead><tr className="bg-slate-50 text-xs text-slate-500 border-b border-slate-100">
                    <th className="text-left px-4 py-2.5">N° Nota</th>
                    <th className="text-left px-4 py-2.5">Fattura collegata</th>
                    <th className="text-left px-4 py-2.5">Committente</th>
                    <th className="text-center px-4 py-2.5">Data</th>
                    <th className="text-right px-4 py-2.5">Importo</th>
                    <th className="text-left px-4 py-2.5">Motivo</th>
                    <th className="px-4 py-2.5"></th>
                  </tr></thead>
                  <tbody className="divide-y divide-slate-100">
                    {noteData.map((nc: any) => (
                      <tr key={nc.id} className="hover:bg-slate-50">
                        <td className="px-4 py-2.5 font-mono text-blue-700 text-xs">{nc.numero}</td>
                        <td className="px-4 py-2.5 font-mono text-slate-500 text-xs">{nc.numero_fattura}</td>
                        <td className="px-4 py-2.5 text-slate-700">{nc.committente_nome}</td>
                        <td className="px-4 py-2.5 text-center text-slate-500 text-xs">{nc.data}</td>
                        <td className="px-4 py-2.5 text-right font-semibold text-red-600">-€{nc.importo.toLocaleString('it-IT', { minimumFractionDigits: 2 })}</td>
                        <td className="px-4 py-2.5 text-slate-500 text-xs max-w-[200px] truncate" title={nc.motivo}>{nc.motivo || '—'}</td>
                        <td className="px-4 py-2.5 text-right">
                          {isAdmin && (
                            <button
                              onClick={() => confirmDelete('Eliminare questa nota credito?', () => deleteNcMut.mutate(nc.id))}
                              className="btn-ghost px-2 py-1 text-red-500 hover:bg-red-50"
                            ><Trash2 size={13} /></button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </>
          )}
        </div>
      )}

      {/* ── TAB: REPORT IVA ──────────────────────────────────────────────── */}
      {tab === 'report-iva' && (
        <div className="space-y-4">
          {ivaLoading ? <div className="card p-8 text-center text-slate-400">Caricamento...</div> : !ivaData ? null : (
            <>
              {/* Totali per aliquota */}
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                {(ivaData.totali_per_aliquota ?? []).map((t: any) => (
                  <div key={t.aliquota_iva} className="card p-4">
                    <div className="text-xs text-slate-500 mb-1">IVA {t.aliquota_iva}% — {t.n_fatture} doc.</div>
                    <div className="text-sm font-bold text-slate-800">Imponibile: €{t.totale_imponibile.toLocaleString('it-IT', { minimumFractionDigits: 2 })}</div>
                    <div className="text-sm font-semibold text-blue-700 mt-0.5">IVA: €{t.totale_iva.toLocaleString('it-IT', { minimumFractionDigits: 2 })}</div>
                  </div>
                ))}
                {(ivaData.totali_per_aliquota ?? []).length === 0 && (
                  <div className="card p-4 col-span-4 text-center text-slate-400 text-sm">Nessun dato per {ivaData.anno}</div>
                )}
              </div>

              {/* Dettaglio per mese */}
              {(ivaData.per_mese ?? []).length > 0 && (
                <div className="card overflow-hidden">
                  <div className="px-4 py-3 border-b border-slate-100">
                    <h3 className="text-sm font-semibold">Dettaglio mensile — {ivaData.anno}</h3>
                  </div>
                  <table className="w-full text-sm">
                    <thead><tr className="bg-slate-50 text-xs text-slate-500">
                      <th className="text-left px-4 py-2">Mese</th>
                      <th className="text-center px-4 py-2">Aliquota IVA</th>
                      <th className="text-right px-4 py-2">Imponibile</th>
                      <th className="text-right px-4 py-2">IVA</th>
                      <th className="text-right px-4 py-2">Totale</th>
                      <th className="text-center px-4 py-2">N° Doc.</th>
                    </tr></thead>
                    <tbody className="divide-y divide-slate-100">
                      {(ivaData.per_mese as any[]).map((r, i) => (
                        <tr key={i} className="hover:bg-slate-50">
                          <td className="px-4 py-2 font-medium">{MESI[r.mese] || `Mese ${r.mese}`}</td>
                          <td className="px-4 py-2 text-center">{r.aliquota_iva}%</td>
                          <td className="px-4 py-2 text-right">€{r.totale_imponibile.toLocaleString('it-IT', { minimumFractionDigits: 2 })}</td>
                          <td className="px-4 py-2 text-right text-blue-700">€{r.totale_iva.toLocaleString('it-IT', { minimumFractionDigits: 2 })}</td>
                          <td className="px-4 py-2 text-right font-semibold">€{(r.totale_imponibile + r.totale_iva).toLocaleString('it-IT', { minimumFractionDigits: 2 })}</td>
                          <td className="px-4 py-2 text-center text-slate-500">{r.n_fatture}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* ── Modal Nuova Nota Credito ─────────────────────────────────────── */}
      {showNcModal && (
        <NuovaNotaCreditoModal
          committenti={committenti || []}
          onClose={() => { setShowNcModal(false); setNcFatturaId(null) }}
          onSuccess={() => { setShowNcModal(false); refetchNote() }}
          initialFatturaId={ncFatturaId}
        />
      )}

      {/* ── Modal Emetti SAL ─────────────────────────────────────────────── */}
      {emittiSalModal && (
        <EmittiSalModal
          salId={emittiSalModal.id}
          dataEmissione={emittiSalModal.dataEmissione}
          committente={emittiSalModal.committente}
          onClose={() => setEmittiSalModal(null)}
          onSuccess={() => {
            setEmittiSalModal(null)
            qc.invalidateQueries({ queryKey: ['sal'] })
            toast.success('SAL emesso e email inviata')
          }}
        />
      )}

      {/* ── Modal Aggiungi Pratiche al SAL ──────────────────────────────── */}
      {aggiungiPraticheId != null && (
        <AggiungiPraticheSalModal
          salId={aggiungiPraticheId}
          daFatturareItems={daFatturareItems}
          onClose={() => setAggiungiPraticheId(null)}
          onSave={(woIds) => aggiungiPraticheMut.mutate({ salId: aggiungiPraticheId!, woIds })}
        />
      )}

      {/* ── Modal Modifica SAL ──────────────────────────────────────────── */}
      {modificaSalId != null && (() => {
        const sal = fattureItems.find((f: any) => f.id === modificaSalId)
        if (!sal) return null
        return (
          <ModificaSalModal
            sal={sal}
            onClose={() => setModificaSalId(null)}
            onSave={(data) => modificaSalMut.mutate({ id: modificaSalId!, data })}
            saving={modificaSalMut.isPending}
          />
        )
      })()}

      {/* ── Modal Crea Fattura ───────────────────────────────────────────── */}
      {showCreaModal && selectedWOData.length > 0 && (
        <CreaFatturaModal
          woItems={selectedWOData}
          committentiList={committenti || []}
          onClose={() => setShowCreaModal(false)}
          onSuccess={() => {
            setShowCreaModal(false)
            setSelectedWO(new Set())
            qc.invalidateQueries({ queryKey: ['da-inserire'] })
            qc.invalidateQueries({ queryKey: ['sal'] })
            setTab('sal')
            toast.success('Fattura creata!')
          }}
        />
      )}
    </div>
  )
}

// ── Modal Nuova Nota Credito ──────────────────────────────────────────────────
function NuovaNotaCreditoModal({ committenti, onClose, onSuccess, initialFatturaId }: {
  committenti: { id: number; ragione_sociale: string }[]
  onClose: () => void
  onSuccess: () => void
  initialFatturaId: number | null
}) {
  const [form, setForm] = useState({
    fattura_id: initialFatturaId ? String(initialFatturaId) : '',
    numero: `NC-${new Date().getFullYear()}-${String(Date.now()).slice(-4)}`,
    data: format(new Date(), 'yyyy-MM-dd'),
    importo: '',
    motivo: '',
    note: '',
  })
  const fld = (k: string, v: string) => setForm(p => ({ ...p, [k]: v }))

  const mutation = useMutation({
    mutationFn: () => fatturazioneApi.noteCredito.create({
      fattura_id: Number(form.fattura_id),
      numero: form.numero,
      data: form.data,
      importo: Number(form.importo),
      motivo: form.motivo || undefined,
      note: form.note || undefined,
    }),
    onSuccess,
    onError: (e: any) => toast.error(e?.response?.data?.detail || 'Errore creazione nota credito'),
  })

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={e => { if (e.target === e.currentTarget) onClose() }}>
      <div className="card w-full max-w-md">
        <div className="p-5 border-b">
          <h2 className="text-base font-semibold">Nuova Nota di Credito</h2>
        </div>
        <div className="p-5 space-y-3">
          <div>
            <label className="label">ID Fattura collegata *</label>
            <input type="number" className="input w-full" value={form.fattura_id} onChange={e => fld('fattura_id', e.target.value)} placeholder="es. 42" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="label">N° Nota credito *</label>
              <input className="input w-full" value={form.numero} onChange={e => fld('numero', e.target.value)} />
            </div>
            <div>
              <label className="label">Data *</label>
              <input type="date" className="input w-full" value={form.data} onChange={e => fld('data', e.target.value)} />
            </div>
          </div>
          <div>
            <label className="label">Importo (€) *</label>
            <input type="number" step="0.01" className="input w-full" value={form.importo} onChange={e => fld('importo', e.target.value)} placeholder="0.00" />
          </div>
          <div>
            <label className="label">Motivo</label>
            <textarea className="input w-full" rows={2} value={form.motivo} onChange={e => fld('motivo', e.target.value)} />
          </div>
        </div>
        <div className="p-5 border-t flex gap-3">
          <button onClick={onClose} className="btn-secondary flex-1">Annulla</button>
          <button
            onClick={() => mutation.mutate()}
            disabled={mutation.isPending || !form.fattura_id || !form.importo}
            className="btn-primary flex-1"
          >
            {mutation.isPending ? 'Salvataggio...' : 'Crea nota credito'}
          </button>
        </div>
      </div>
    </div>
  )
}

// ── Modal Crea SAL ────────────────────────────────────────────────────────────
function CreaFatturaModal({ woItems, committentiList, onClose, onSuccess }: {
  woItems: any[]
  committentiList: { id: number; ragione_sociale: string }[]
  onClose: () => void
  onSuccess: () => void
}) {
  const committenteId = woItems[0]?.committente_id
  const committente = committentiList.find(c => c.id === committenteId)
  const produzione = woItems.reduce((s: number, w: any) => s + (w.valore_stimato || 0), 0)
  const today = format(new Date(), 'yyyy-MM-dd')

  const [form, setForm] = useState({
    numero_sal: `SAL-${new Date().getFullYear()}-${String(Date.now()).slice(-4)}`,
    data_emissione: today,
    note: '',
  })
  const fld = (k: string, v: string) => setForm(p => ({ ...p, [k]: v }))

  const [vociManuali, setVociManuali] = useState<
    { descrizione: string; quantita: number; prezzo_unitario: number }[]
  >([])
  const aggiungiVoce = () =>
    setVociManuali(prev => [...prev, { descrizione: '', quantita: 1, prezzo_unitario: 0 }])
  const rimuoviVoce = (idx: number) =>
    setVociManuali(prev => prev.filter((_, i) => i !== idx))
  const aggiornaVoce = (idx: number, campo: string, val: string | number) =>
    setVociManuali(prev => prev.map((v, i) => i === idx ? { ...v, [campo]: val } : v))

  const totaleVociManuali = vociManuali.reduce((s, v) => s + v.quantita * v.prezzo_unitario, 0)
  const totaleFinale = produzione + totaleVociManuali

  const mutation = useMutation({
    mutationFn: () => fatturazioneApi.create({
      committente_id: committenteId,
      work_order_ids: woItems.map((w: any) => w.id),
      righe: [],
      imponibile: 0,
      iva_percentuale: 0,
      numero_fattura: form.numero_sal,
      data_emissione: form.data_emissione,
      note: form.note || undefined,
      voci_manuali: vociManuali.filter(v => v.descrizione.trim()),
      tipo_documento: 'sal',
    } as any),
    onSuccess,
    onError: (e: any) => toast.error(e?.response?.data?.detail || 'Errore creazione SAL'),
  })

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={e => { if (e.target === e.currentTarget) onClose() }}>
      <div className="card w-full max-w-xl max-h-[90vh] overflow-y-auto">
        <div className="p-5 border-b">
          <h2 className="text-base font-semibold">Crea SAL</h2>
          <p className="text-xs text-slate-500 mt-0.5">{committente?.ragione_sociale} — {woItems.length} pratiche</p>
        </div>
        <div className="p-5 space-y-4">
          {/* Pratiche */}
          <div className="rounded-lg border border-slate-200 overflow-hidden">
            <table className="w-full text-xs">
              <thead><tr className="bg-slate-50">
                <th className="text-left px-3 py-2">N° Pratica</th>
                <th className="text-left px-3 py-2">Tipologia</th>
                <th className="text-right px-3 py-2">Valore</th>
              </tr></thead>
              <tbody className="divide-y divide-slate-100">
                {woItems.map((w: any) => (
                  <tr key={w.id}>
                    <td className="px-3 py-2 font-mono text-blue-700">{w.numero_progressivo}</td>
                    <td className="px-3 py-2 text-slate-600 capitalize">{w.tipologia?.replace(/_/g,' ') || '—'}</td>
                    <td className="px-3 py-2 text-right font-medium">{w.valore_stimato > 0 ? `€${w.valore_stimato.toFixed(2)}` : '—'}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot><tr className="bg-slate-50 font-semibold">
                <td className="px-3 py-2" colSpan={2}>Produzione</td>
                <td className="px-3 py-2 text-right text-green-700">€{produzione.toFixed(2)}</td>
              </tr></tfoot>
            </table>
          </div>

          {/* Voci manuali */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-semibold text-slate-600 uppercase tracking-wide">Voci Aggiuntive</span>
              <button onClick={aggiungiVoce} className="btn-ghost px-2 py-1 text-xs flex items-center gap-1 text-blue-600">
                <Plus size={12} /> Aggiungi voce
              </button>
            </div>
            {vociManuali.length === 0 ? (
              <p className="text-xs text-slate-400 italic py-2">Nessuna voce aggiuntiva. Clicca "+ Aggiungi voce" per inserirne una.</p>
            ) : (
              <div className="space-y-2">
                {vociManuali.map((v, idx) => (
                  <div key={idx} className="flex gap-2 items-center">
                    <input className="input flex-1 text-sm" placeholder="Descrizione voce..."
                      value={v.descrizione} onChange={e => aggiornaVoce(idx, 'descrizione', e.target.value)} />
                    <input className="input w-16 text-sm text-center" type="number" min="1" step="1"
                      placeholder="Qtà" value={v.quantita}
                      onChange={e => aggiornaVoce(idx, 'quantita', Number(e.target.value))} />
                    <input className="input w-24 text-sm text-right" type="number" min="0" step="0.01"
                      placeholder="€ 0,00" value={v.prezzo_unitario}
                      onChange={e => aggiornaVoce(idx, 'prezzo_unitario', Number(e.target.value))} />
                    <span className="text-xs text-slate-500 w-20 text-right">€ {(v.quantita * v.prezzo_unitario).toFixed(2)}</span>
                    <button onClick={() => rimuoviVoce(idx)} className="btn-ghost px-1.5 py-1 text-red-400 hover:text-red-600">
                      <Trash2 size={13} />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="label">N° SAL *</label>
              <input className="input" value={form.numero_sal} onChange={e => fld('numero_sal', e.target.value)} required />
            </div>
            <div>
              <label className="label">Data Emissione *</label>
              <input type="date" className="input" value={form.data_emissione} onChange={e => fld('data_emissione', e.target.value)} required />
            </div>
          </div>

          <div>
            <label className="label">Note</label>
            <textarea className="input" rows={2} value={form.note} onChange={e => fld('note', e.target.value)} />
          </div>

          {/* Riepilogo */}
          <div className="rounded-lg bg-slate-50 p-3 space-y-1 text-sm">
            <div className="flex justify-between"><span className="text-slate-500">Produzione ({woItems.length} pratiche)</span><span>€{produzione.toFixed(2)}</span></div>
            {totaleVociManuali !== 0 && (
              <div className="flex justify-between"><span className="text-slate-500">Voci aggiuntive</span><span>€{totaleVociManuali.toFixed(2)}</span></div>
            )}
            <div className="flex justify-between font-bold text-base pt-1 border-t border-slate-200">
              <span>Totale SAL</span><span className="text-green-700">€{totaleFinale.toFixed(2)}</span>
            </div>
          </div>
        </div>

        <div className="px-5 pb-5 flex gap-3">
          <button onClick={onClose} className="btn-secondary flex-1">Annulla</button>
          <button onClick={() => mutation.mutate()} disabled={mutation.isPending || !form.numero_sal} className="btn-primary flex-1">
            {mutation.isPending ? 'Creazione...' : 'Crea SAL'}
          </button>
        </div>
      </div>
    </div>
  )
}

// ── Modal Aggiungi Pratiche SAL ───────────────────────────────────────────────
function AggiungiPraticheSalModal({ salId, daFatturareItems, onClose, onSave }: {
  salId: number
  daFatturareItems: any[]
  onClose: () => void
  onSave: (woIds: number[]) => void
}) {
  const [sel, setSel] = useState<Set<number>>(new Set())
  const toggle = (id: number) => setSel(prev => {
    const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n
  })
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
      onClick={onClose}>
      <div style={{ background: '#fff', borderRadius: 14, width: 520, maxHeight: '75vh', display: 'flex', flexDirection: 'column', overflow: 'hidden', boxShadow: '0 20px 60px rgba(0,0,0,0.3)' }}
        onClick={e => e.stopPropagation()}>
        <div style={{ padding: '16px 20px 12px', borderBottom: '1px solid #f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <span style={{ fontSize: 15, fontWeight: 700 }}>Aggiungi pratiche al SAL #{salId}</span>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#9ca3af', fontSize: 18 }}>✕</button>
        </div>
        {daFatturareItems.length === 0 ? (
          <div style={{ padding: 32, textAlign: 'center', color: '#9ca3af', fontSize: 13 }}>Nessuna pratica da fatturare disponibile</div>
        ) : (
          <div style={{ overflowY: 'auto', flex: 1 }}>
            <table style={{ width: '100%', fontSize: 12 }}>
              <thead><tr style={{ background: '#f8fafc', borderBottom: '1px solid #e5e7eb' }}>
                <th style={{ width: 36, padding: '8px 12px' }}></th>
                <th style={{ textAlign: 'left', padding: '8px 12px' }}>N° WO</th>
                <th style={{ textAlign: 'left', padding: '8px 12px' }}>Committente</th>
                <th style={{ textAlign: 'left', padding: '8px 12px' }}>Comune</th>
                <th style={{ textAlign: 'right', padding: '8px 12px' }}>€</th>
              </tr></thead>
              <tbody>
                {daFatturareItems.map((w: any) => (
                  <tr key={w.id} onClick={() => toggle(w.id)} style={{ cursor: 'pointer', background: sel.has(w.id) ? '#eff6ff' : 'transparent', borderBottom: '1px solid #f8fafc' }}>
                    <td style={{ padding: '7px 12px', textAlign: 'center' }}>
                      <input type="checkbox" checked={sel.has(w.id)} readOnly />
                    </td>
                    <td style={{ padding: '7px 12px', fontFamily: 'monospace', fontWeight: 600, color: '#1E40AF' }}>{w.numero_progressivo}</td>
                    <td style={{ padding: '7px 12px' }}>{w.committente_nome}</td>
                    <td style={{ padding: '7px 12px' }}>{w.cliente_comune}</td>
                    <td style={{ padding: '7px 12px', textAlign: 'right' }}>{w.valore_stimato > 0 ? `€${w.valore_stimato.toFixed(2)}` : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <div style={{ padding: '12px 20px', borderTop: '1px solid #f1f5f9', display: 'flex', gap: 10 }}>
          <button onClick={onClose} style={{ flex: 1, padding: '9px', borderRadius: 8, border: '1px solid #e5e7eb', background: '#fff', cursor: 'pointer', fontSize: 13 }}>Annulla</button>
          <button onClick={() => onSave(Array.from(sel))} disabled={sel.size === 0}
            style={{ flex: 1, padding: '9px', borderRadius: 8, background: '#1E40AF', color: '#fff', border: 'none', cursor: sel.size === 0 ? 'default' : 'pointer', fontSize: 13, fontWeight: 700, opacity: sel.size === 0 ? 0.5 : 1 }}>
            Aggiungi {sel.size > 0 ? `${sel.size} pratiche` : ''}
          </button>
        </div>
      </div>
    </div>
  )
}

// ── Modal Modifica SAL ──────────────────────────────────────────────────────
interface SalRiga {
  tipo: 'pratica' | 'manuale'
  wo_id?: number
  numero_pratica?: string
  descrizione?: string
  denominazione_sito?: string
  comune?: string
  committente?: string
  tipologia?: string
  quantita: number
  prezzo_unitario: number
  totale: number
}

function ModificaSalModal({ sal, onClose, onSave, saving }: {
  sal: any
  onClose: () => void
  onSave: (data: {
    numero_sal?: number | null; numero_fattura: string; iva_percentuale?: number
    data_emissione: string | null; data_scadenza: string | null
    periodo_da: string | null; periodo_a: string | null
    righe?: SalRiga[]
  }) => void
  saving: boolean
}) {
  const isSal = sal.tipo_documento === 'sal'
  const [numeroSal, setNumeroSal] = useState<string>(sal.numero_sal != null ? String(sal.numero_sal) : '')
  const [numeroFattura, setNumeroFattura] = useState<string>(sal.numero_fattura ?? '')
  const [ivaPercentuale, setIvaPercentuale] = useState<string>(String(sal.iva_percentuale ?? 22))
  const [dataEmissione, setDataEmissione] = useState<string>(sal.data_emissione ?? '')
  const [dataScadenza, setDataScadenza] = useState<string>(sal.data_scadenza ?? '')
  const [periodoDa, setPeriodoDa] = useState<string>(sal.periodo_da ?? '')
  const [periodoA, setPeriodoA] = useState<string>(sal.periodo_a ?? '')
  const [righe, setRighe] = useState<SalRiga[]>(
    (sal.righe || []).map((r: any) => ({ ...r, quantita: Number(r.quantita ?? 1), prezzo_unitario: Number(r.prezzo_unitario ?? 0) }))
  )

  const updateRiga = (i: number, patch: Partial<SalRiga>) => {
    setRighe(prev => prev.map((r, idx) => idx === i ? { ...r, ...patch } : r))
  }
  const rimuoviRiga = (i: number) => setRighe(prev => prev.filter((_, idx) => idx !== i))
  const aggiungiRigaManuale = () => setRighe(prev => [...prev, { tipo: 'manuale', descrizione: '', quantita: 1, prezzo_unitario: 0, totale: 0 }])

  const imponibile = righe.reduce((s, r) => s + (Number(r.quantita) || 0) * (Number(r.prezzo_unitario) || 0), 0)
  const ivaNum = parseFloat(ivaPercentuale) || 0
  const ivaImporto = imponibile * ivaNum / 100
  const totale = imponibile + ivaImporto

  const salva = () => {
    onSave({
      numero_fattura: numeroFattura.trim(),
      data_emissione: dataEmissione || null,
      data_scadenza: dataScadenza || null,
      periodo_da: periodoDa || null,
      periodo_a: periodoA || null,
      ...(isSal ? {
        numero_sal: numeroSal.trim() ? parseInt(numeroSal, 10) : null,
        iva_percentuale: ivaNum,
        righe: righe.map(r => ({
          ...r,
          quantita: Number(r.quantita) || 0,
          prezzo_unitario: Number(r.prezzo_unitario) || 0,
        })),
      } : {}),
    })
  }

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
      onClick={onClose}>
      <div style={{ background: '#fff', borderRadius: 14, width: 760, maxHeight: '85vh', display: 'flex', flexDirection: 'column', overflow: 'hidden', boxShadow: '0 20px 60px rgba(0,0,0,0.3)' }}
        onClick={e => e.stopPropagation()}>
        <div style={{ padding: '16px 20px 12px', borderBottom: '1px solid #f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <span style={{ fontSize: 15, fontWeight: 700 }}>Modifica {isSal ? 'SAL' : 'fattura'} {sal.numero_fattura}</span>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#9ca3af' }}><XIcon size={18} /></button>
        </div>

        {isSal && sal.stato === 'emessa' && (
          <div style={{ padding: '8px 20px', background: '#fffbeb', color: '#92400e', fontSize: 12 }}>
            ⚠️ Questo SAL è già emesso. Salvando le modifiche tornerà in bozza e dovrà essere ri-emesso/reinviato.
          </div>
        )}

        <div style={{ padding: '14px 20px', display: 'flex', gap: 16, flexWrap: 'wrap', borderBottom: '1px solid #f1f5f9' }}>
          <div>
            <label style={{ fontSize: 11, fontWeight: 600, color: '#64748b', display: 'block', marginBottom: 4 }}>Numero documento</label>
            <input type="text" className="input" style={{ width: 160 }} value={numeroFattura} onChange={e => setNumeroFattura(e.target.value)} />
          </div>
          {isSal && (
            <div>
              <label style={{ fontSize: 11, fontWeight: 600, color: '#64748b', display: 'block', marginBottom: 4 }}>N° SAL</label>
              <input type="number" className="input" style={{ width: 90 }} value={numeroSal} onChange={e => setNumeroSal(e.target.value)} />
            </div>
          )}
          {isSal && (
            <div>
              <label style={{ fontSize: 11, fontWeight: 600, color: '#64748b', display: 'block', marginBottom: 4 }}>IVA %</label>
              <input type="number" className="input" style={{ width: 80 }} value={ivaPercentuale} onChange={e => setIvaPercentuale(e.target.value)} />
            </div>
          )}
          <div>
            <label style={{ fontSize: 11, fontWeight: 600, color: '#64748b', display: 'block', marginBottom: 4 }}>Data emissione</label>
            <input type="date" className="input" style={{ width: 150 }} value={dataEmissione} onChange={e => setDataEmissione(e.target.value)} />
          </div>
          <div>
            <label style={{ fontSize: 11, fontWeight: 600, color: '#64748b', display: 'block', marginBottom: 4 }}>Data scadenza</label>
            <input type="date" className="input" style={{ width: 150 }} value={dataScadenza} onChange={e => setDataScadenza(e.target.value)} />
          </div>
          <div>
            <label style={{ fontSize: 11, fontWeight: 600, color: '#64748b', display: 'block', marginBottom: 4 }}>Periodo da</label>
            <input type="date" className="input" style={{ width: 150 }} value={periodoDa} onChange={e => setPeriodoDa(e.target.value)} />
          </div>
          <div>
            <label style={{ fontSize: 11, fontWeight: 600, color: '#64748b', display: 'block', marginBottom: 4 }}>Periodo a</label>
            <input type="date" className="input" style={{ width: 150 }} value={periodoA} onChange={e => setPeriodoA(e.target.value)} />
          </div>
        </div>

        {isSal && (
        <div style={{ overflowY: 'auto', flex: 1 }}>
          <table style={{ width: '100%', fontSize: 12 }}>
            <thead><tr style={{ background: '#f8fafc', borderBottom: '1px solid #e5e7eb', position: 'sticky', top: 0 }}>
              <th style={{ textAlign: 'left', padding: '8px 12px' }}>Riga</th>
              <th style={{ textAlign: 'right', padding: '8px 12px', width: 80 }}>Qtà</th>
              <th style={{ textAlign: 'right', padding: '8px 12px', width: 110 }}>Prezzo unit.</th>
              <th style={{ textAlign: 'right', padding: '8px 12px', width: 100 }}>Totale</th>
              <th style={{ width: 36 }}></th>
            </tr></thead>
            <tbody>
              {righe.map((r, i) => (
                <tr key={i} style={{ borderBottom: '1px solid #f8fafc' }}>
                  <td style={{ padding: '6px 12px' }}>
                    {r.tipo === 'pratica' ? (
                      <div>
                        <span style={{ fontFamily: 'monospace', fontWeight: 600, color: '#1E40AF' }}>{r.numero_pratica}</span>
                        <div style={{ fontSize: 11, color: '#94a3b8' }}>{r.denominazione_sito || r.comune} {r.tipologia ? `· ${r.tipologia}` : ''}</div>
                      </div>
                    ) : (
                      <input
                        className="input" style={{ width: '100%' }}
                        placeholder="Descrizione voce manuale"
                        value={r.descrizione || ''}
                        onChange={e => updateRiga(i, { descrizione: e.target.value })}
                      />
                    )}
                  </td>
                  <td style={{ padding: '6px 12px' }}>
                    <input type="number" step="0.01" className="input" style={{ width: '100%', textAlign: 'right' }}
                      value={r.quantita} onChange={e => updateRiga(i, { quantita: parseFloat(e.target.value) || 0 })} />
                  </td>
                  <td style={{ padding: '6px 12px' }}>
                    <input type="number" step="0.01" className="input" style={{ width: '100%', textAlign: 'right' }}
                      value={r.prezzo_unitario} onChange={e => updateRiga(i, { prezzo_unitario: parseFloat(e.target.value) || 0 })} />
                  </td>
                  <td style={{ padding: '6px 12px', textAlign: 'right', fontWeight: 600 }}>
                    €{((Number(r.quantita) || 0) * (Number(r.prezzo_unitario) || 0)).toFixed(2)}
                  </td>
                  <td style={{ padding: '6px 12px', textAlign: 'center' }}>
                    <button onClick={() => rimuoviRiga(i)} title="Rimuovi riga" style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#ef4444' }}>
                      <Trash2 size={14} />
                    </button>
                  </td>
                </tr>
              ))}
              {righe.length === 0 && (
                <tr><td colSpan={5} style={{ padding: 24, textAlign: 'center', color: '#9ca3af' }}>Nessuna riga — il SAL risulterebbe vuoto</td></tr>
              )}
            </tbody>
          </table>
          <div style={{ padding: '8px 20px' }}>
            <button onClick={aggiungiRigaManuale} className="btn-ghost text-xs flex items-center gap-1" style={{ color: '#1E40AF' }}>
              <Plus size={13} /> Aggiungi riga manuale
            </button>
          </div>
        </div>
        )}

        <div style={{ padding: '12px 20px', borderTop: '1px solid #f1f5f9', background: '#f8fafc' }}>
          {isSal && (
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 20, fontSize: 12, marginBottom: 10 }}>
              <span>Imponibile <strong>€{imponibile.toFixed(2)}</strong></span>
              <span>IVA ({ivaNum}%) <strong>€{ivaImporto.toFixed(2)}</strong></span>
              <span style={{ color: '#15803d' }}>Totale <strong>€{totale.toFixed(2)}</strong></span>
            </div>
          )}
          <div style={{ display: 'flex', gap: 10 }}>
            <button onClick={onClose} style={{ flex: 1, padding: '9px', borderRadius: 8, border: '1px solid #e5e7eb', background: '#fff', cursor: 'pointer', fontSize: 13 }}>Annulla</button>
            <button onClick={salva} disabled={saving || (isSal && righe.length === 0)}
              style={{ flex: 1, padding: '9px', borderRadius: 8, background: '#1E40AF', color: '#fff', border: 'none', cursor: saving ? 'default' : 'pointer', fontSize: 13, fontWeight: 700, opacity: saving || (isSal && righe.length === 0) ? 0.5 : 1 }}>
              {saving ? 'Salvataggio...' : 'Salva modifiche'}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

// ── Modal Emetti SAL con email ────────────────────────────────────────────────
const MESI_IT = ['gennaio','febbraio','marzo','aprile','maggio','giugno',
  'luglio','agosto','settembre','ottobre','novembre','dicembre']

function EmittiSalModal({ salId, dataEmissione, committente, onClose, onSuccess }: {
  salId: number
  dataEmissione: string
  committente: string
  onClose: () => void
  onSuccess: () => void
}) {
  const dataSal = dataEmissione ? new Date(dataEmissione) : new Date()
  const meseSal = MESI_IT[dataSal.getMonth()]
  const annoSal = dataSal.getFullYear()

  const DESTINATARI_DEFAULT =
    'rodolfo.conte@italtel.com, andrea.alari@italtel.com, ' +
    'walter.gattuso@deltaict.it, stefano.sborchia@deltaict.it, segreteria@deltaict.it'

  const corpoDefault =
    `Spett.le ${committente || 'Committente'}\n\n` +
    `si trasmette riepilogo stato avanzamento lavori svolti nel mese di ${meseSal} ${annoSal}\n\n` +
    `attendiamo benestare a fatturare\n\n` +
    `Cordialità\nDelta Srl\nsegreteria@deltaict.it`

  const [destinatari, setDestinatari] = useState(DESTINATARI_DEFAULT)
  const [corpo, setCorpo] = useState(corpoDefault)
  const [sending, setSending] = useState(false)

  const handleInvia = async () => {
    const emails = destinatari.split(/[,;\n]/).map(e => e.trim()).filter(Boolean)
    if (!emails.length) { alert('Inserire almeno un indirizzo email'); return }
    setSending(true)
    try {
      await fatturazioneApi.emittiSal(salId, emails, corpo)
      onSuccess()
    } catch (e: any) {
      alert(e?.response?.data?.detail || 'Errore invio email')
    } finally {
      setSending(false)
    }
  }

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}
      onClick={onClose}>
      <div style={{ background: '#fff', borderRadius: 16, width: '100%', maxWidth: 500, boxShadow: '0 20px 60px rgba(0,0,0,0.25)', overflow: 'hidden' }}
        onClick={e => e.stopPropagation()}>
        <div style={{ padding: '20px 24px 14px', borderBottom: '1px solid #f1f5f9' }}>
          <div style={{ fontSize: 16, fontWeight: 700, color: '#1E40AF', marginBottom: 2 }}>
            📤 Emetti SAL e invia email
          </div>
          <div style={{ fontSize: 12, color: '#64748b' }}>
            Il SAL verrà emesso e il PDF allegato all'email
          </div>
        </div>

        <div style={{ padding: '18px 24px', display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div>
            <label style={{ fontSize: 12, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 6 }}>
              Destinatari email *
            </label>
            <textarea
              rows={2}
              placeholder="email1@azienda.it, email2@azienda.it"
              value={destinatari}
              onChange={e => setDestinatari(e.target.value)}
              style={{ width: '100%', padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 13, resize: 'vertical', boxSizing: 'border-box' }}
            />
            <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 3 }}>
              Separare più indirizzi con virgola o a capo
            </div>
          </div>

          <div>
            <label style={{ fontSize: 12, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 6 }}>
              Testo email
            </label>
            <textarea
              rows={8}
              value={corpo}
              onChange={e => setCorpo(e.target.value)}
              style={{ width: '100%', padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 13, resize: 'vertical', fontFamily: 'inherit', boxSizing: 'border-box' }}
            />
          </div>
        </div>

        <div style={{ padding: '12px 24px 20px', display: 'flex', gap: 10 }}>
          <button onClick={onClose}
            style={{ flex: 1, padding: 10, borderRadius: 8, border: '1px solid #e5e7eb', background: '#fff', cursor: 'pointer', fontSize: 14, fontWeight: 600, color: '#374151' }}>
            Annulla
          </button>
          <button onClick={handleInvia} disabled={sending || !destinatari.trim()}
            style={{ flex: 2, padding: 10, borderRadius: 8, background: sending || !destinatari.trim() ? '#93c5fd' : '#1E40AF', color: '#fff', border: 'none', cursor: sending || !destinatari.trim() ? 'default' : 'pointer', fontSize: 14, fontWeight: 700 }}>
            {sending ? '⏳ Invio in corso...' : '📤 Emetti e invia email'}
          </button>
        </div>
      </div>
    </div>
  )
}
